import { StarIcon } from 'lucide-react';
import { RING_CHIPS, RingChip, type RingKey } from './RingChip.js';
import { useTenantBrand } from '../lib/tenant-brand.js';

/**
 * Visuele legenda onder de welkomsttekst: de sterbeoordeling en de
 * bronchips precies zoals ze onder een Mila-antwoord staan.
 *
 * Waarom code en geen tekst: de bewerkbare tekst gaat door react-markdown,
 * en dat stript `data:`-afbeeldingen (safeProtocol in defaultUrlTransform).
 * Iconen in de tekst renderen dus niet, en een tekstuele beschrijving van
 * chipkleuren loopt achter zodra er een laag bijkomt. Door hier dezelfde
 * RingChip te gebruiken als de citatielijst kan dat niet meer gebeuren.
 *
 * Alleen voor accounts met Mila: zonder AI-tools is er geen antwoord om
 * te beoordelen en geen bron om te herkennen.
 */
export function PanelLegenda(): JSX.Element {
  const { eigen } = useTenantBrand();
  // Een andere tenant ziet de interne SUPPOHRT-lagen nooit in Mila (ADR RHOGO,
  // alleen ring 1-3), dus hoort de legenda ze ook niet uit te leggen.
  const lagen = (Object.keys(RING_CHIPS) as RingKey[]).filter(
    (k) => !eigen || (k !== 'ring0' && k !== 'ring_hq'),
  );
  return (
    <section className="mt-6 rounded-2xl border border-ring2 bg-white p-5">
      <h3 className="text-sm font-bold text-ink-900">Zo ziet een antwoord van Mila eruit</h3>

      <div className="mt-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5" aria-hidden="true">
            {[1, 2, 3, 4, 5].map((n) => (
              <StarIcon
                key={n}
                size={15}
                className={n <= 4 ? 'fill-mosterdgeel-500 text-mosterdgeel-500' : 'text-ink-100'}
              />
            ))}
          </div>
          <span className="text-[13px] font-semibold text-ink-900">Sterbeoordeling</span>
        </div>
        <p className="mt-1.5 text-[13px] leading-relaxed text-ink-900/70">
          Onder elk antwoord. Geef een oordeel van 1 tot 5 sterren, met een korte toelichting als er
          iets niet klopt. Die beoordelingen komen in één wachtrij binnen en bepalen wat we aan de
          kennisbank en aan Mila verbeteren.
        </p>
      </div>

      <div className="mt-5">
        <span className="text-[13px] font-semibold text-ink-900">Bronnen</span>
        <p className="mt-1.5 text-[13px] leading-relaxed text-ink-900/70">
          Onderaan elk antwoord staan de bronnen waarop het gebaseerd is. De chip ernaast laat zien
          uit welke laag een bron komt. Klik de titel aan om het originele document of de bronpagina
          te openen.
        </p>
        <dl className="mt-3 space-y-2.5">
          {lagen.map((key) => (
            <div key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <dt className="shrink-0">
                <RingChip ring={key} />
              </dt>
              <dd className="min-w-0 flex-1 text-[13px] leading-relaxed text-ink-900/70">
                {RING_CHIPS[key].uitleg}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-[12px] leading-relaxed text-ink-900/55">
          De onderste twee lagen zie je alleen als je rol daar toegang toe geeft.
        </p>
      </div>
    </section>
  );
}
